// Room model — constants, types and pure helpers for the Opportunity Room.
//
// No React, no JSX: everything here is data or a pure function, so the derive
// layer (derive.ts), the signals engine (derive-signals.ts) and the chapters
// can share one source of truth and be reasoned about in isolation from
// rendering.
//
// Reading data notes:
//   • `metadata.source` is the channel discriminator (mock-only today; real
//     BE doesn't stamp it — see design log). Missing source = service drive.
//   • Category $ splits derive from line-item operations as proportions
//     normalized to the estimate's value; the 18% industry close rate and
//     margins are business constants from the CEO's model, not API data.

import type { ApiEstimate, ApiOperation, VehicleSection } from "@/lib/api/types"

/* ── Business constants (CEO model — not API data) ────── */

export const INDUSTRY_CLOSE_RATE = 18 // % — industry average close rate benchmark
export const HIGH_VALUE_FLOOR = 3000 // $ — "high-value job" threshold
export const AGING_HOURS = 24
export const LABOR_RATE = 95 // $/h blended body labor rate for $ mix estimation
export const PAINT_MATERIALS_RATE = 40 // $/refinish-hour — standard materials formula
export const TECH_WEEK_HOURS = 40 // h — one tech-week of shop load (body-shop framing)

export type CategoryKey = "labor" | "parts" | "paint" | "sublet"
/** Fallback revenue mix when an estimate has no operation-level data. */
export const MODEL_MIX = { labor: 0.4, parts: 0.4, paint: 0.08, sublet: 0.12 }
export const CATEGORY_MARGINS: Record<CategoryKey, number> = { labor: 55, parts: 25, paint: 50, sublet: 30 }
export const CATEGORY_LABELS: Record<CategoryKey, string> = {
  labor: "Labor", parts: "Parts", paint: "Paint Materials", sublet: "Sublet",
}

/* ── Palette (validated: see design log — dataviz six checks) ── */

export const INK = "#0D1117"
export const MINT = "#5DCAA5"
export const MINT_PALE = "#9FE1CB"
export const FOAM = "#E1F5EE"
export const INK_MINT = "#0F6E56" // deep mint for text-on-light / active nav underline
export const CAT = { deep: "#007A52", brand: "#1D9E75", light: "#5DCAA5", gray: "#71717A" }
export const RAMP = ["#4EC29A", "#26A97E", "#0E8F63", "#00734C"]

/* ── Sources & ranges ─────────────────────────────────── */

export type SourceKey = "service_drive" | "used_car_appraisal" | "lease_turn_in" | "consumer_intake"
export const SOURCES: { key: SourceKey; label: string }[] = [
  { key: "service_drive", label: "Service drive" },
  { key: "used_car_appraisal", label: "Used car appraisal" },
  { key: "lease_turn_in", label: "Lease turn-in" },
  { key: "consumer_intake", label: "Consumer intake" },
]
export const SOURCE_LABEL = (k: SourceKey | null) => SOURCES.find(s => s.key === k)?.label ?? null

// Hero stacked-spark mint ramp on ink (channel identity carried by the legend
// beneath — the stack shows mix shape, tooltips are not needed there).
export const CHANNEL_SPARK: Record<SourceKey, string> = {
  service_drive: "#1D9E75", used_car_appraisal: "#5DCAA5",
  lease_turn_in: "#9FE1CB", consumer_intake: "#E1F5EE",
}

export const sourceOf = (e: ApiEstimate): SourceKey => {
  const s = (e.metadata as Record<string, unknown> | null)?.source
  return SOURCES.some(x => x.key === s) ? (s as SourceKey) : "service_drive"
}

export type RangeKey = "24h" | "7d" | "30d" | "90d"
export const RANGES: Record<RangeKey, { label: string; days: number; buckets: number }> = {
  "24h": { label: "24h", days: 1, buckets: 12 },
  "7d": { label: "7d", days: 7, buckets: 7 },
  "30d": { label: "30d", days: 30, buckets: 30 },
  "90d": { label: "90d", days: 90, buckets: 13 },
}

export const TIERS = [
  { label: "< $1k", min: 0, max: 1000, color: RAMP[0] },
  { label: "$1k–$2k", min: 1000, max: 2000, color: RAMP[1] },
  { label: "$2k–$3k", min: 2000, max: 3000, color: RAMP[2] },
  { label: "$3k–$4k", min: 3000, max: 4000, color: RAMP[3] },
  { label: "$4k–$5k", min: 4000, max: 5000, color: RAMP[3] },
  { label: "$5k+", min: 5000, max: Infinity, color: RAMP[3] },
]

export const SECTION_LABELS: Record<VehicleSection, string> = {
  FRONT_BUMPER_GRILLE: "Front bumper/grille", REAR_BUMPER: "Rear bumper", FENDER: "Fender",
  FRONT_DOOR: "Front door", REAR_DOOR: "Rear door", HOOD: "Hood", TRUNK: "Trunk", ROOF: "Roof",
  FRONT_LAMPS: "Front lamps", REAR_LAMPS: "Rear lamps", QUARTER_PANEL: "Quarter panel",
  ROCKER_PANEL: "Rocker panel", LIFT_GATE: "Lift gate", TAIL_GATE: "Tail gate", WHEELS: "Wheels",
  FRAME: "Frame", TRUNK_LID: "Trunk lid", PICKUP_BOX: "Pickup box", WINDSHIELD: "Windshield",
}

// Named-part labels from the BE's UPPER_SNAKE `component` enum. Side prefixes
// (LT/RT/LF/RF/LR/RR) are spelled out so the parts chapter reads for every
// department, not just body-shop staff who parse "LT" fluently.
const SIDE_PREFIX: Record<string, string> = {
  LT: "Left", RT: "Right", LF: "Left front", RF: "Right front", LR: "Left rear", RR: "Right rear",
}
export function componentLabel(component: string): string {
  const tokens = component.split("_")
  let prefix = ""
  if (tokens.length > 1 && SIDE_PREFIX[tokens[0]]) {
    prefix = SIDE_PREFIX[tokens[0]]
    tokens.shift()
  }
  const body = tokens
    .map(t => (t === "ASY" ? "assembly" : t.toLowerCase()))
    .join(" ")
  const label = [prefix, body].filter(Boolean).join(" ").trim()
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : component
}

/* ── Chapters (the room's reading order + department homes) ── */

export type Dept = "service" | "parts" | "bodyshop" | "leadership" | "network"
export type ChapterId =
  | "lane" | "converting" | "work" | "parts" | "makes" | "paint" | "people" | "stores"

/** `audience` labels whose home each chapter is; `groupOnly` hides it in single-store view. */
export const CHAPTERS: { id: ChapterId; label: string; audience?: string; groupOnly?: boolean }[] = [
  { id: "lane", label: "The lane", audience: "For the lane" },
  { id: "converting", label: "Converting", audience: "For leadership" },
  { id: "work", label: "The work", audience: "For the booth" },
  { id: "parts", label: "Parts", audience: "For the parts desk" },
  { id: "makes", label: "Makes & models", audience: "For leadership" },
  { id: "paint", label: "Paint", audience: "For the booth" },
  { id: "people", label: "Who & when", audience: "For the lane" },
  { id: "stores", label: "Stores", audience: "For leadership", groupOnly: true },
]
export const CHAPTER_LABEL: Record<ChapterId, string> = Object.fromEntries(
  CHAPTERS.map(c => [c.id, c.label]),
) as Record<ChapterId, string>

/* ── Role-tailored views (same room, different emphasis) ── */
// The meetings are explicit: a service manager wants "who's using it and when",
// not part sales; the body shop wants "here's the opportunity and where we are",
// kept simple. Leadership gets the full picture. One codebase, role-driven set.
export type ViewMode = "full" | "service" | "bodyshop"
const VIEW_CHAPTERS: Record<ViewMode, ChapterId[]> = {
  full: ["lane", "converting", "work", "parts", "makes", "paint", "people", "stores"],
  service: ["people", "lane", "converting"], // adoption first
  bodyshop: ["lane", "converting", "people"], // opportunity, simple
}
/** Ordered chapters for a view; the first is the landing chapter. `stores` is dropped unless in group view. */
export function roomConfig(viewMode: ViewMode, showStores: boolean): { chapters: ChapterId[]; landing: ChapterId } {
  const chapters = VIEW_CHAPTERS[viewMode].filter(c => c !== "stores" || showStores)
  return { chapters, landing: chapters[0] ?? "lane" }
}

/* ── Small helpers ────────────────────────────────────── */

export const fmt$ = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${Math.round(n).toLocaleString()}`
}
export const fmt$full = (n: number) => `$${Math.round(n).toLocaleString()}`
export const pct = (curr: number, prev: number) => (prev === 0 ? (curr > 0 ? 100 : 0) : Math.round(((curr - prev) / prev) * 100))
export const median = (xs: number[]) => {
  if (!xs.length) return 0
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}
export const HEADLINE = { fontFamily: "var(--font-headline)" } as const

export const isOpen = (e: ApiEstimate) => ["Pending", "Confirmed", "InProgress", "Scheduled"].includes(e.status)
export const ageHours = (e: ApiEstimate) => (Date.now() - new Date(e.createdAt).getTime()) / 3600000
export const fmtAge = (h: number) => (h < 48 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`)
export const isStructural = (e: ApiEstimate) => e.lineItems?.some(li => li.structuralInvolvement) ?? false

export const AXIS_TICK = { fontSize: 11, fill: "#71717a" }
export const GRID_STROKE = "rgba(0,0,0,0.06)"

export const REFINISH_TYPES = new Set(["refinish", "refinish_prep", "refinish_adder"])

/**
 * Revenue mix of one estimate — operations give proportions, avg gives scale.
 * Returns the scale factor too, so per-component parts/paint derivations can
 * attribute operation dollars on the exact same normalization the category
 * donut uses (the parts card total then reconciles with the Parts slice).
 */
export function categoryMixWithScale(e: ApiEstimate): {
  mix: Record<CategoryKey, number>; scale: number; hasOps: boolean
} {
  const v = e.avg ?? 0
  if (v <= 0) return { mix: { labor: 0, parts: 0, paint: 0, sublet: 0 }, scale: 0, hasOps: false }
  const modelMix = { labor: v * MODEL_MIX.labor, parts: v * MODEL_MIX.parts, paint: v * MODEL_MIX.paint, sublet: v * MODEL_MIX.sublet }
  const ops: ApiOperation[] = e.lineItems?.flatMap(li => li.operations ?? []) ?? []
  if (ops.length === 0) return { mix: modelMix, scale: 0, hasOps: false }
  const raw: Record<CategoryKey, number> = { labor: 0, parts: 0, paint: 0, sublet: 0 }
  for (const op of ops) {
    raw.labor += op.laborHours * LABOR_RATE
    if (op.workType === "sublet_other") raw.sublet += op.partPrice
    else raw.parts += op.partPrice
    raw.sublet += op.subletAmount ?? 0
    if (REFINISH_TYPES.has(op.workType)) raw.paint += op.laborHours * PAINT_MATERIALS_RATE
  }
  const rawTotal = raw.labor + raw.parts + raw.paint + raw.sublet
  if (rawTotal <= 0) return { mix: modelMix, scale: 0, hasOps: false }
  const scale = v / rawTotal
  return {
    mix: { labor: raw.labor * scale, parts: raw.parts * scale, paint: raw.paint * scale, sublet: raw.sublet * scale },
    scale, hasOps: true,
  }
}
export const categoryMix = (e: ApiEstimate): Record<CategoryKey, number> => categoryMixWithScale(e).mix

/* ── Cross-filter model ───────────────────────────────── */

export type DimKey = "source" | "advisor" | "store" | "tier" | "section" | "make"
export type DimFilters = {
  source: SourceKey | null
  advisorId: string
  /** When advisorId encodes a department ("dept:X"), the resolved member
   *  ids — matchDim then matches ANY of them instead of one person. */
  advisorIdSet?: Set<string> | null
  storeId: string
  tier: string // '' or index into TIERS
  section: string // '' or VehicleSection
  make: string // '' or vehicle.make
}

export function matchDim(e: ApiEstimate, f: DimFilters, skip?: DimKey): boolean {
  if (skip !== "source" && f.source && sourceOf(e) !== f.source) return false
  if (skip !== "advisor" && f.advisorId) {
    const id = String(e.createdBy?.id ?? "")
    if (f.advisorIdSet) {
      if (!f.advisorIdSet.has(id)) return false
    } else if (id !== f.advisorId) return false
  }
  if (skip !== "store" && f.storeId && String(e.dealership?.id ?? "") !== f.storeId) return false
  if (skip !== "tier" && f.tier !== "") {
    const t = TIERS[Number(f.tier)]
    const v = e.avg ?? 0
    if (!t || v < t.min || v >= t.max) return false
  }
  if (skip !== "section" && f.section && !e.lineItems?.some(li => li.section === f.section)) return false
  if (skip !== "make" && f.make && (e.vehicle?.make ?? "") !== f.make) return false
  return true
}
